import { useParams } from 'react-router-dom';
import { ServiceCard } from '@/components/service-page/ServiceCard';
import { SearchAndCategories } from '@/components/service-page/SearchAndCategories';
import { useUnclaimedPage } from '@/utils/useUnclaimedPage';
import { useToast } from "@/components/ui/use-toast";
import { useAnalytics } from "@/hooks/useAnalytics";
import { ServiceCardSkeleton } from '@/components/service-page/ServiceCardSkeleton';
import { SearchAndCategoriesSkeleton } from '@/components/service-page/SearchAndCategoriesSkeleton';
import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { MetaTags } from '@/components/shared/MetaTags';
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Crown } from "lucide-react";
import { BusinessProfile } from '@/components/service-page/BusinessProfile';
import { ServiceBannerSkeleton } from '@/components/service-page/ServiceBannerSkeleton';

const UnclaimedPage = () => {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const { trackAction } = useAnalytics({ component: "UnclaimedPage", pageView: true });
  const { t } = useTranslation();
  
  const [showSkeleton, setShowSkeleton] = useState(true);
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      console.log('UnclaimedPage mounted');
      setMounted(true);
    }, 200); 
    
    return () => clearTimeout(timer);
  }, []);
  
  const {
    profile,
    services,
    categories,
    isLoading,
    unclaimedPageNotFound,
    searchTerm,
    categoryId,
    setSearchTerm,
    setCategoryId,
    handleImageNavigation,
  } = useUnclaimedPage(id || '');

  useEffect(() => {
    console.log('UnclaimedPage loading state changed:', { isLoading, servicesLength: services?.length || 0, mounted, profile: !!profile });
    
    if (!mounted || isLoading) {
      setShowSkeleton(true);
      return;
    }
    
    if (!isLoading && services !== undefined && profile !== null && mounted) {
      const timer = setTimeout(() => {
        console.log('Showing unclaimed content after delay');
        setShowSkeleton(false);
      }, 300);
      
      return () => clearTimeout(timer);
    }
    
    setShowSkeleton(true);
  }, [isLoading, services, mounted, profile]);

  const handleServiceSelect = (serviceId: string) => {
    trackAction("unclaimed_service_selection", { serviceId });
    
    toast({
      title: "This is an unclaimed business",
      description: "This business hasn't claimed their page yet. Contact them directly to request services.",
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.trim() !== searchTerm.trim()) {
      trackAction("unclaimed_service_search", { searchTerm: e.target.value });
    }
    
    setSearchTerm(e.target.value);
  };

  const handleClaimBusiness = () => {
    trackAction("claim_business_click", { businessId: id });
    
    toast({
      title: "Claim This Business",
      description: "Contact us to claim this business and start managing your online presence.",
    });
  };

  console.log('UnclaimedPage render', { 
    id, 
    unclaimedPageNotFound, 
    isLoading, 
    showSkeleton,
    servicesLength: services?.length || 0,
    hasProfile: !!profile
  });

  if (unclaimedPageNotFound) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gradient-to-b from-pink-50 to-white">
        <h1 className="text-3xl font-bold text-gray-800 mb-4">Unclaimed Business Not Found</h1>
        <p className="text-gray-600">The unclaimed business page you're looking for doesn't exist.</p>
      </div>
    );
  }

  // Parse social links from JSON
  const parseSocialLinks = (socialLinksJson: any) => {
    if (!socialLinksJson) return [];
    
    try {
      const links = typeof socialLinksJson === 'string' ? JSON.parse(socialLinksJson) : socialLinksJson;
      
      return Object.entries(links).map(([platform, url]) => ({
        id: platform,
        platform: platform.toLowerCase(),
        url: url as string,
        enabled: true,
        username: '',
        location: ''
      }));
    } catch (error) {
      console.error('Error parsing social links:', error);
      return [];
    }
  };

  const socialLinks = profile ? parseSocialLinks(profile.social_links) : [];

  return (
    <div className="min-h-screen bg-gray-50">
      {profile && (
        <MetaTags
          title={`${profile.business_name || profile.service_provider_name} - Unclaimed Business`}
          description={profile.bio || `Unclaimed business profile for ${profile.business_name || profile.service_provider_name}`}
          imageUrl={profile.banner_image || profile.profile_picture || "/lovable-uploads/f3752283-ff9d-4cc9-b3c2-b633673f0e58.png"}
          url={window.location.href}
          siteName={profile.business_name || profile.service_provider_name || "Unclaimed Business"}
        />
      )}
      
      {/* Unclaimed Business Banner */}
      <div className="relative">
        {/* Custom banner section for unclaimed businesses */}
        {!showSkeleton && profile ? (
          <div className="relative w-full">
            {/* Banner Image */}
            {profile.banner_image && (
              <div className="w-full relative overflow-hidden rounded-lg">
                <div className="w-full" style={{ paddingTop: '30%' }}>
                  <img
                    loading="lazy"
                    src={profile.banner_image}
                    alt="Banner"
                    className="absolute top-0 left-0 w-full h-full object-cover rounded-lg"
                  />
                </div>
              </div>
            )}
            
            <div className="container mx-auto max-w-5xl px-4">
              {/* Profile Image */}
              {profile.profile_picture && (
                <div 
                  className="flex justify-center relative z-10" 
                  style={{ marginTop: profile.banner_image ? '-80px' : '20px' }}
                >
                  <img
                    loading="lazy"
                    src={profile.profile_picture}
                    alt={profile.service_provider_name || profile.business_name || "Profile"}
                    className="w-32 h-32 rounded-full border-4 border-white shadow-lg object-cover"
                  />
                </div>
              )}
              
              {/* Business Profile with all contact info */}
              <div className="mt-4">
                <BusinessProfile
                  businessName={profile.business_name || ""}
                  profileImage=""
                  onShare={handleClaimBusiness}
                  firstName={profile.service_provider_name?.split(' ')[0] || null}
                  lastName={profile.service_provider_name?.split(' ').slice(1).join(' ') || null}
                  profession={profile.profession}
                  profileBio={profile.bio}
                  country={profile.country}
                  address={profile.address}
                  phoneNumber={profile.phone_number}
                  socialLinks={socialLinks}
                  preferredMessaging="whatsapp"
                  hideProfileImage={true}
                />
              </div>
            </div>
          </div>
        ) : (
          <ServiceBannerSkeleton />
        )}
        
        {/* Unclaimed Badge Overlay */}
        {!showSkeleton && profile && (
          <div className="absolute top-4 right-4 z-10">
            <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-300">
              <Crown className="w-4 h-4 mr-1" />
              Unclaimed Business
            </Badge>
          </div>
        )}
      </div>

      {/* Claim Business Call-to-Action */}
      {!showSkeleton && profile && (
        <div className="bg-gradient-to-r from-gebeya-pink to-gebeya-orange text-white py-4">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-lg font-semibold mb-2">Is this your business?</h2>
            <p className="text-sm mb-4">Claim this page to manage your online presence and connect with customers.</p>
            <Button 
              onClick={handleClaimBusiness}
              variant="secondary"
              className="bg-white text-gebeya-pink hover:bg-gray-100"
            >
              Claim This Business
            </Button>
          </div>
        </div>
      )}

      <main className="container mx-auto px-2 sm:px-4 py-4 sm:py-6">
        {showSkeleton ? (
          <SearchAndCategoriesSkeleton />
        ) : (
          <SearchAndCategories 
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            categories={categories}
            selectedCategoryId={categoryId}
            onCategoryChange={setCategoryId}
          />
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 mt-4">
          {showSkeleton ? (
            <>
              <ServiceCardSkeleton />
              <ServiceCardSkeleton />
              <ServiceCardSkeleton />
            </>
          ) : services.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <h3 className="text-lg font-medium text-gray-700">No services listed</h3>
              <p className="text-gray-500 mt-2">This business hasn't added any services yet.</p>
            </div>
          ) : (
            services.map(service => (
              <ServiceCard
                key={service.id}
                service={service}
                serviceImages={service.images}
                selectedImageIndex={service.selectedImageIndex}
                onImageNavigation={(direction) => {
                  handleImageNavigation(service.id, direction);
                }}
                onServiceSelect={() => handleServiceSelect(service.id)}
                currency={profile?.currency}
                businessName={profile?.business_name}
              />
            ))
          )}
        </div>
      </main>
    </div>
  );
};

export default UnclaimedPage; 